import { faTrash, faUndo } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { useEffect, useState, ReactNode, useCallback } from "react";
import { Button } from "react-bootstrap";
import { Row, Cell } from "react-table";
import CommentDialog from "../../Dialogs/CommentDialog";
import ConfirmationDialog from "../../Dialogs/ConfirmationDialog";
import { Changes } from "../../Types/Changes";
import DataTable from "../datatable.component";

// exposes key fields of the editable datatable to allow the components it renders to interact with the tables data,
// when they are provided from a parent component which will have no inherit knowledge of the tables internal workings
export interface EditableDatatableAPI<T extends object> {
    updateData: (accessor: keyof T, row: Row<T>, value: any) => void;
    deleteData: (row: Row<T>) => void;
    createData: (item: T) => void;
    applyToFilteredRows: (accessor: keyof T, value: any, clearFilters?: boolean) => void;
    tableData: T[];
    filteredRows: Row<T>[];
}

interface EditableDatatableProps<T extends object> {
    identifier: keyof T,
    tableColumns: any[],
    initialTableData: T[],
    initialState?: any,
    getRowProps?: any,
    getCellProps?: any,
    autoResetPage?: boolean,
    showPaging?: boolean,
    canDelete?: boolean,
    children?: ReactNode | ((api: EditableDatatableAPI<T>) => ReactNode),
    saveChanges: (data: Changes<T>, comment: string) => void;
}

function EditableDatatable<T extends object>({ identifier, tableColumns, initialTableData, initialState, getRowProps, getCellProps, autoResetPage, showPaging, canDelete = false, children, saveChanges }: EditableDatatableProps<T>) {
    const [tableData, setTableData] = useState(initialTableData);
    const [changes, setChanges] = useState<Changes<T>>({ updates: [], deletes: [], creates:[] });
    const [hasChanges, setHasChanges] = useState(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [filteredRows, setFilteredRows] = useState<Row<T>[]>([]);

    useEffect(() => {
        setTableData(initialTableData);
    }, [initialTableData]);

    const deleteColumn = {
        id: 'deleteColumn',
        Cell: (props: Cell<T>) => {
            const showAsDelete = changes.deletes.findIndex(del => del[identifier] === props.row.original[identifier]) === -1;
            return <Button variant={"outline-light"} onClick={() => deleteData(props.row)} className={"border-0"}>
                {showAsDelete
                    ? <FontAwesomeIcon icon={faTrash} className="text-danger"/>
                    : <FontAwesomeIcon icon={faUndo} className="text-primary"/>
                }
            </Button>;
        },
        disableFilters: true,
        disableSortBy: true,
        className: "w-auto"
    };

    const setFilteredRowsSafely = useCallback((rows: Row<T>[]) => {
        setFilteredRows(prev => {
            if (prev.length === rows.length) {
                // compare by row.original deep equality (simple approach)
                let equal = true;
                for (let i = 0; i < rows.length; i++) {
                    try {
                        if (JSON.stringify(prev[i].original) !== JSON.stringify(rows[i].original)) {
                            equal = false;
                            break;
                        }
                    } catch {
                        equal = false;
                        break;
                    }
                }
                if (equal) return prev;
            }
            return rows;
        });
    }, []);

    const updateData = useCallback((accessor: keyof T, row: Row<T>, value: any) => {
        const originalRow = initialTableData.find(r => r[identifier] === row.original[identifier]);
        // Row not in original data, must be from a create
        if (originalRow === undefined) {
            setChanges(prev => {
                const existingCreateIndex = prev.creates.findIndex(create => create[identifier] === row.original[identifier]);
                const isTrackedCreate = existingCreateIndex >= 0;

                // Update the create record, don't create a new update record
                if (isTrackedCreate) {
                    let newCreates = prev.creates;
                    newCreates = prev.creates.map(create => create[identifier] === row.original[identifier] ? { ...create, [accessor]: value } : create);
                    return { ...prev, creates: newCreates };
                } else {
                    return prev;
                }
            });

            return;
        }

        // Update update tracking
        setChanges(prev => {
            const existingUpdateIndex = prev.updates.findIndex(update => update[identifier] === row.original[identifier]);
            const isTrackedUpdate = existingUpdateIndex >= 0;

            let newUpdates = prev.updates;
            // Change back to original value, remove change from tracked changes
            if (originalRow[accessor] === value) {
                if (isTrackedUpdate) {
                    const existingUpdate = prev.updates[existingUpdateIndex];
                    let keys = Object.keys(originalRow) as (keyof T)[];
                    // All changes for row have been undone, remove from tracking
                    if (keys.every((key) => key === accessor || originalRow[key] === existingUpdate[key])) {
                        newUpdates = prev.updates.filter(update => update[identifier] !== row.original[identifier]);
                    } else { // Some changes for row still exist, need to keep tracking remaining changes
                        newUpdates = prev.updates.map(update => update[identifier] === row.original[identifier] ? { ...update, [accessor]: value } : update);
                    }
                }
            } else { // Change from original value, track the change
                // Already tracking some changes for row, update tracked changes for row
                if (isTrackedUpdate) {
                    newUpdates = prev.updates.map(update => update[identifier] === row.original[identifier] ? { ...update, [accessor]: value } : update);
                } else { // New changes to row, add row to change tracking
                    newUpdates = [...prev.updates, { ...originalRow, [accessor]: value }];
                }
            }

            return { ...prev, updates: newUpdates };
        });

        // Update data shown in table
        setTableData(data => [...data.map(r => r[identifier] === row.original[identifier] ? { ...r, [accessor]: value } : r)]);
    }, [identifier, initialTableData]);

    const deleteData = useCallback((row: Row<T>) => {
        const originalRow = initialTableData.find(r => r[identifier] === row.original[identifier]);
        // Row not in original data, must be from a create
        if (originalRow === undefined) {
            setChanges(prev => {
                const existingCreateIndex = prev.creates.findIndex(create => create[identifier] === row.original[identifier]);
                const isTrackedCreate = existingCreateIndex >= 0;

                // Remove the create record, don't create a new delete record
                if (isTrackedCreate) {
                    let newCreates = prev.creates;
                    newCreates = prev.creates.filter(create => create[identifier] !== row.original[identifier]);
                    setTableData(data => data.filter(x => x[identifier] !== row.original[identifier]));

                    return { ...prev, creates: newCreates };
                } else {
                    return prev;
                }
            });

            return;
        }

        // Update delete tracking
        setChanges(prev => {
            const existingDeleteIndex = prev.deletes.findIndex(del => del[identifier] === row.original[identifier]);
            const isAlreadyDeleted = existingDeleteIndex !== -1
            let newDeletes = prev.deletes;

            // New Delete, add to tracked deletes
            if (!isAlreadyDeleted) {
                newDeletes = [...prev.deletes, { ...originalRow }];
            } else { // Delete already tracked, undo delete by removing from tracked deletes
                newDeletes = prev.deletes.filter(del => del[identifier] !== row.original[identifier]);
            }

            return { ...prev, deletes: newDeletes };
        });
    }, [identifier, initialTableData]);

    const createData = useCallback((item: T) => {
        const existingItemIndex = initialTableData.findIndex(x => x[identifier] === item[identifier]);
        const itemIsNew = existingItemIndex === -1;
        if (!itemIsNew) { // Item is not new so should be being updated
            return;
        }

        setChanges(prev => {
            const existingCreateIndex = prev.creates.findIndex(add => add[identifier] === item[identifier]);
            const itemIsAlreadyTracked = existingCreateIndex !== -1;
            let newCreates = prev.creates;

            // New Add, add to tracked deletes
            if (!itemIsAlreadyTracked) {
                newCreates = [...prev.creates, { ...item }];
            } else { // Add already tracked, replace tracked add with new add
                newCreates = prev.creates.map(add => add[identifier] !== item[identifier] ? add : { ...item });
            }

            return { ...prev, creates: newCreates };
        });

        // Add new item into the table data being tracked
        setTableData((data) => {
            let dataIndex = data.findIndex(x => x[identifier] === item[identifier]);
            if (dataIndex === -1) {
                return [...data, item];
            } else {
                return [...data.map(r => r[identifier] === item[identifier] ? { ...item } : r)];
            }
        });
    }, [identifier, initialTableData]);

    useEffect(() => {
        // Update has change flag
        setHasChanges(changes.updates.length > 0 || changes.deletes.length > 0 || changes.creates.length > 0);
    }, [changes.updates.length, changes.deletes.length, changes.creates.length]);

    const [resetFiltersToken, setResetFiltersToken] = useState(0);
    const applyToFilteredRows = useCallback((accessor: keyof T, value: any, clearFilters: boolean = false) => {
        filteredRows.forEach(row => {
            updateData(accessor, row, value);
        });

        if (clearFilters) {
            setResetFiltersToken(t => t + 1);
        }
    }, [filteredRows, updateData]);

    const handleUndo = useCallback(() => {
        setTableData(initialTableData);
        setChanges({ updates: [], deletes: [], creates:[] });
        setHasChanges(false);
    }, [initialTableData]);

    const handleSaveWithComment = useCallback((comment: string) => {
        saveChanges(changes, comment);
    }, [changes, saveChanges]);

    const renderChildren = useCallback((children: ReactNode | ((api: EditableDatatableAPI<T>) => ReactNode)) => {
        if (typeof children === 'function') {
            return children({ updateData, createData, deleteData, applyToFilteredRows, tableData, filteredRows });
        }

        return children;
    }, [applyToFilteredRows, deleteData, filteredRows, tableData, updateData, createData]);

    let columns = tableColumns;
    if (canDelete) {
        columns = [...tableColumns, deleteColumn];
    }

    const editableTableGetRowProps = (row: Row<T>) => {

        let rowProps = getRowProps ? getRowProps(row) : {};

        let className = rowProps?.className;
        if (changes.updates.findIndex(update => update[identifier] === row.original[identifier]) >= 0) {
            className = "bg-info bg-opacity-75"
        }
        if (changes.creates.findIndex(add => add[identifier] === row.original[identifier]) >= 0) {
            className = "bg-success bg-opacity-75"
        }
        if (changes.deletes.findIndex(del => del[identifier] === row.original[identifier]) >= 0) {
            className = "bg-danger bg-opacity-75"
        }

        let editProps = { className: className }

        let result = { ...rowProps, ...editProps }

        return result;
    }

    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const checkChanges = () => {
        if (changes.deletes.length > 0) {
            setShowConfirmationDialog(true);
        } else {
            setShowCommentDialog(true);
        }
    };

    return (
        <>
            <DataTable tableColumns={columns} tableData={tableData} initialState={initialState} autoResetPage={false} getCellProps={getCellProps} updateData={updateData} setFilteredRows={setFilteredRowsSafely} resetFiltersToken={resetFiltersToken} getRowProps={editableTableGetRowProps}>
                {renderChildren(children)}
            </DataTable>
            <div className="d-flex">
                <Button className="ms-auto" variant="secondary" hidden={!hasChanges} onClick={handleUndo}>Undo</Button>
                <Button className="ms-2" hidden={!hasChanges} onClick={() => checkChanges()}>Save</Button>
            </div>
            <ConfirmationDialog title={t<string>("Datatable.Confirmation.Title")} showDialog={showConfirmationDialog} closeDialog={() => setShowConfirmationDialog(false)} onOk={() => setShowCommentDialog(true) } variant="warning">
                <p>{t<string>("Datatable.Confirmation.Warning", { items: changes.deletes.map(i => i[identifier]).join(', ') })}</p>
            </ConfirmationDialog>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={handleSaveWithComment} />
        </>
    );
}

export default EditableDatatable;